import React, { useMemo, useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';

import { DataGrid, KeenIcon } from '@/components';
import { Loading } from '@/layouts/hettwer/loading';
import { formatDateOnlyWithRelative } from '@/pages/hettwer/utils/Date';
import { DeleteOfferConfirmationModal } from '@/pages/hettwer/offer/blocks/delete-offer/DeleteOfferModal';
import { getSensitiveFieldClassName, usePrivacyMode } from '@/utils/privacyMode';

/* ===============================
   LIST TYPE (FLAT, FAST)
   =============================== */

export interface OfferListItem {
  p_id: string;
  offer_number?: string | null;
  offer_date?: string | null;
  status: string;
  customer_name?: string | null;
  is_training_data?: boolean;
}

/* ===============================
   PROPS
   =============================== */

interface OffersProps {
  offers: OfferListItem[];
  loading: boolean;
  on_delete: (offerId: string) => void;
}

/* ===============================
   COMPONENT
   =============================== */

const Offers: React.FC<OffersProps> = ({ offers, loading, on_delete }) => {
  const storageFilterId = 'offers-filter';
  const navigate = useNavigate();
  const { isPrivacyMode, togglePrivacyMode } = usePrivacyMode();

  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem(storageFilterId) || '');

  useEffect(() => {
    localStorage.setItem(storageFilterId, searchTerm);
  }, [searchTerm]);

  /* ===============================
     FILTER (CLIENT SIDE)
     =============================== */

  const filteredData = useMemo(() => {
    const search = searchTerm.toLowerCase();

    return offers.filter((offer) => {
      return (
        (offer.offer_number || '').toLowerCase().includes(search) ||
        (offer.customer_name || '').toLowerCase().includes(search)
      );
    });
  }, [offers, searchTerm]);

  /* ===============================
     DELETE MODAL
     =============================== */

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferListItem | null>(null);

  const handleDeleteClick = (offer: OfferListItem) => {
    setSelectedOffer(offer);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedOffer) {
      on_delete(selectedOffer.p_id);
    }
    setModalOpen(false);
  };

  /* ===============================
     COLUMNS
     =============================== */

  const columns = useMemo<ColumnDef<OfferListItem>[]>(
    () => [
      {
        accessorKey: 'offer_number',
        header: () => 'Angebotsnummer',
        cell: ({ row }) => (
          <button
            className="text-sm font-medium text-gray-900 hover:text-primary"
            onClick={() => navigate(`/offer/${row.original.p_id}`)}
          >
            {row.original.offer_number || '—'}
          </button>
        )
      },
      {
        accessorKey: 'offer_date',
        header: () => 'Angebotsdatum',
        cell: ({ getValue }) => formatDateOnlyWithRelative(getValue<string | null>())
      },
      {
        accessorKey: 'customer_name',
        header: () => 'Kunde',
        cell: ({ getValue }) => (
          <span className={`text-sm text-gray-800 ${getSensitiveFieldClassName(isPrivacyMode)}`}>
            {getValue<string>() || '—'}
          </span>
        )
      },
      {
        id: 'status',
        header: () => 'Status',
        cell: ({ row }) => {
          const isFinal = row.original.status === 'final';
          const isTraining = row.original.is_training_data === true;

          return (
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                  isFinal
                    ? 'bg-green-100 text-green-800 border-green-300'
                    : 'bg-yellow-100 text-yellow-800 border-yellow-300'
                }`}
              >
                {isFinal ? 'Fertig' : 'Entwurf'}
              </span>

              {isTraining && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-300">
                  <KeenIcon icon="award" className="text-xs" />
                  Training
                </span>
              )}
            </div>
          );
        }
      },
      {
        id: 'open',
        header: () => '',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            className="btn btn-sm btn-icon btn-clear btn-light"
            onClick={() => navigate(`/offer/${row.original.p_id}`)}
          >
            <KeenIcon icon="notepad-edit" />
          </button>
        )
      },
      {
        id: 'delete',
        header: () => '',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            className="btn btn-sm btn-icon btn-clear btn-light"
            onClick={() => handleDeleteClick(row.original)}
          >
            <KeenIcon icon="trash" />
          </button>
        )
      }
    ],
    [isPrivacyMode, navigate]
  );

  if (loading) return <Loading loading={loading} />;

  /* ===============================
     RENDER
     =============================== */

  return (
    <>
      <div className="card card-grid min-w-full">
        <div className="card-header flex-wrap py-5 gap-3">
          <h3 className="card-title">Angebote</h3>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={togglePrivacyMode}
              aria-pressed={isPrivacyMode}
              className={`btn btn-sm ${isPrivacyMode ? 'btn-warning' : 'btn-light'}`}
            >
              <KeenIcon icon={isPrivacyMode ? 'eye-slash' : 'eye'} />
              {isPrivacyMode ? 'Personendaten verborgen' : 'Personendaten sichtbar'}
            </button>

            <div className="relative">
              <KeenIcon
                icon="magnifier"
                className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
              />
              <input
                type="text"
                placeholder="Angebote suchen"
                className="input input-sm pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card-body">
          <DataGrid
            key={searchTerm}
            columns={columns}
            data={filteredData}
            pagination={{ size: 10 }}
            sorting={[{ id: 'offer_number', desc: false }]}
          />
        </div>
      </div>

      {selectedOffer && (
        <DeleteOfferConfirmationModal
          open={isModalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmDelete}
          offerNumber={selectedOffer.offer_number || ''}
        />
      )}
    </>
  );
};

export { Offers };
